import React, { useState, useEffect, useMemo, useCallback } from 'react';
import APIService from '../../services/api';

/**
 * Barème des coûts employeur — la base du CJM.
 *
 * Ce barème n'appartient à aucun écran : il alimente le CJM d'un contrat de
 * ressource, la marge d'une mission et, demain, la comptabilité. Il vit donc
 * dans la configuration de l'organisation, une fois, et tout le reste le lit.
 *
 * Le CALCUL fait foi côté serveur. Ce que fait cet écran, c'est saisir les
 * paramètres et montrer immédiatement leur effet sur un prix de journée —
 * sinon on règle des pourcentages à l'aveugle. Quand la route de simulation
 * existe, c'est elle qui répond ; sinon l'écran estime, et le dit.
 *
 * Routes : GET/PUT /config/couts ; POST /config/couts/simulation.
 * Absentes (404/405/501) : l'écran le dit et n'écrit rien.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

const LBL = {
  fontFamily: 'var(--erp-font-mono)', fontSize: '9.5px', fontWeight: 700,
  letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--erp-text-tertiary)',
};
const num = (v) => (v === '' || v == null ? 0 : Number(v) || 0);
const chf = (v) => `${Math.round(num(v)).toLocaleString('fr-CH').replace(/ | /g, ' ')} CHF`;
const chf2 = (v) => `${num(v).toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/ | /g, ' ')} CHF`;

/* Structure du barème suisse. Les libellés et les tranches d'âge LPP sont la
   charpente légale ; les TAUX ne sont pas préremplis — ils changent chaque
   année et selon la caisse, et un chiffre inventé serait pris pour argent
   comptant. Les placeholders donnent l'ordre de grandeur, rien de plus. */
const LIGNES_STD = [
  { cle: 'avs', libelle: 'AVS / AI / APG', indice: '5.3' },
  { cle: 'ac', libelle: 'Assurance chômage', indice: '1.1', plafondIndice: '148200' },
  { cle: 'laa', libelle: 'LAA — accidents professionnels', indice: '0.8', plafondIndice: '148200' },
  { cle: 'ijm', libelle: 'Indemnités journalières maladie', indice: '1.5' },
  { cle: 'af', libelle: 'Allocations familiales', indice: '1.8' },
  { cle: 'admin', libelle: 'Frais administratifs de caisse', indice: '0.3' },
];
const TRANCHES_STD = [
  { age_min: 25, age_max: 34, taux: '' },
  { age_min: 35, age_max: 44, taux: '' },
  { age_min: 45, age_max: 54, taux: '' },
  { age_min: 55, age_max: 65, taux: '' },
];
const INDICE_TRANCHE = { 25: '7', 35: '10', 45: '15', 55: '18' };
const VIDE = {
  taux: LIGNES_STD.map(l => ({ cle: l.cle, libelle: l.libelle, taux: '', plafond: '' })),
  lpp: { seuil_acces: '', deduction_coordination: '', salaire_max: '', coordonne_min: '', part_employeur: '50', tranches: TRANCHES_STD },
  frais: { annuel: '', mensuel: '', journalier: '' },
  jours_ouvres: '225',
};

/** Salaire coordonné LPP — le seul calcul non linéaire du barème. */
export const salaireCoordonne = (brut, lpp = {}) => {
  const b = num(brut);
  const seuil = num(lpp.seuil_acces);
  if (seuil && b < seuil) return 0;
  const plafond = num(lpp.salaire_max);
  const plafonne = plafond ? Math.min(b, plafond) : b;
  const coord = plafonne - num(lpp.deduction_coordination);
  const plancher = num(lpp.coordonne_min);
  if (coord <= 0) return plancher ? plancher : 0;
  return Math.max(coord, plancher);
};

/** Estimation locale — le serveur fait foi dès qu'il répond. */
export const estimerCout = (bareme, { salaire, age, joursOuvres }) => {
  const brut = num(salaire);
  const detail = [];
  let charges = 0;
  (bareme.taux || []).forEach((l) => {
    const t = num(l.taux);
    if (!t) return;
    const assiette = num(l.plafond) ? Math.min(brut, num(l.plafond)) : brut;
    const montant = (assiette * t) / 100;
    charges += montant;
    detail.push({ libelle: l.libelle, note: `${t} %${num(l.plafond) ? ` jusqu'à ${chf(l.plafond)}` : ''}`, montant });
  });
  const lpp = bareme.lpp || {};
  const coord = salaireCoordonne(brut, lpp);
  const tranche = (lpp.tranches || []).find(t => age >= num(t.age_min) && age <= num(t.age_max)) || null;
  const tauxLpp = num(tranche?.taux);
  const part = num(lpp.part_employeur) || 50;
  const montantLpp = (coord * tauxLpp * part) / 10000;
  if (montantLpp) detail.push({ libelle: 'LPP (part employeur)', note: `${tauxLpp} % de ${chf(coord)} · ${part} % employeur`, montant: montantLpp });
  const f = bareme.frais || {};
  const jours = num(joursOuvres) || num(bareme.jours_ouvres) || 1;
  const fraisAnnuels = num(f.annuel) + num(f.mensuel) * 12 + num(f.journalier) * jours;
  if (fraisAnnuels) detail.push({ libelle: 'Frais entreprise', note: [num(f.annuel) && `${chf(f.annuel)}/an`, num(f.mensuel) && `${chf(f.mensuel)}/mois`, num(f.journalier) && `${chf(f.journalier)}/jour`].filter(Boolean).join(' · '), montant: fraisAnnuels });
  const coutAnnuel = brut + charges + montantLpp + fraisAnnuels;
  return {
    brut, charges, lpp: montantLpp, coordonne: coord, tranche, frais: fraisAnnuels,
    cout_annuel: coutAnnuel, cjm: jours ? coutAnnuel / jours : 0,
    coefficient: brut ? coutAnnuel / brut : 0,
    detail, jours,
  };
};

export default function CoutsEmployeur({ showNotification }) {
  const [b, setB] = useState(null);          // null = chargement
  const [orig, setOrig] = useState(null);
  const [absent, setAbsent] = useState(false);
  const [configure, setConfigure] = useState(true);   // le serveur le dit
  const [busy, setBusy] = useState(false);
  const [champErreur, setChampErreur] = useState(null);   // { champ, message }
  // Cas d'essai : ce qui rend les pourcentages lisibles.
  const [essai, setEssai] = useState({ salaire: '130000', age: '38', jours: '' });
  const [serveur, setServeur] = useState(null);   // simulation renvoyée par le serveur

  const charger = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/config/couts`, { headers: authH() });
      if ([404, 405, 501].includes(r.status)) { setAbsent(true); setB(VIDE); setOrig(VIDE); return; }
      const j = await r.json().catch(() => ({}));
      const raw = j.data ?? j;
      const d = raw.value ?? raw;
      // On repose la charpente : une ligne standard absente du serveur doit
      // rester saisissable, sinon un taux oublié devient invisible.
      const parCle = Object.fromEntries((d.taux || []).map(l => [l.cle, l]));
      const taux = LIGNES_STD.map(l => ({
        cle: l.cle, libelle: parCle[l.cle]?.libelle || l.libelle,
        taux: parCle[l.cle]?.taux ?? '', plafond: parCle[l.cle]?.plafond ?? '',
      }));
      (d.taux || []).filter(l => !LIGNES_STD.some(s => s.cle === l.cle)).forEach(l => taux.push({ cle: l.cle, libelle: l.libelle || l.cle, taux: l.taux ?? '', plafond: l.plafond ?? '' }));
      const lppSrv = d.lpp || {};
      const valeur = {
        taux,
        lpp: {
          seuil_acces: lppSrv.seuil_acces ?? '', deduction_coordination: lppSrv.deduction_coordination ?? '',
          salaire_max: lppSrv.salaire_max ?? '', coordonne_min: lppSrv.coordonne_min ?? '',
          part_employeur: lppSrv.part_employeur ?? '50',
          tranches: Array.isArray(lppSrv.tranches) && lppSrv.tranches.length ? lppSrv.tranches : TRANCHES_STD,
        },
        frais: { annuel: d.frais?.annuel ?? '', mensuel: d.frais?.mensuel ?? '', journalier: d.frais?.journalier ?? '' },
        jours_ouvres: d.jours_ouvres ?? '225',
      };
      setConfigure(d.configure !== false);
      setB(valeur); setOrig(valeur);
    } catch { setB(VIDE); setOrig(VIDE); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const dirty = b && orig && JSON.stringify(b) !== JSON.stringify(orig);
  const maj = (patch) => setB(v => ({ ...v, ...patch }));
  const majTaux = (i, patch) => setB(v => ({ ...v, taux: v.taux.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const majLpp = (patch) => setB(v => ({ ...v, lpp: { ...v.lpp, ...patch } }));
  const majTranche = (i, patch) => setB(v => ({ ...v, lpp: { ...v.lpp, tranches: v.lpp.tranches.map((t, j) => (j === i ? { ...t, ...patch } : t)) } }));

  const enregistrer = async () => {
    setBusy(true); setChampErreur(null);
    try {
      const r = await fetch(`${BASE}/config/couts`, { method: 'PUT', headers: jsonH(), body: JSON.stringify({ value: b }) });
      const j = await r.json().catch(() => ({}));
      if ([404, 405, 501].includes(r.status) && !j.error) {
        showNotification?.('Le barème des coûts arrive côté serveur — rien n\'a été enregistré.', 'info');
        return;
      }
      if (!r.ok || j.success === false) {
        const msg = j.error || j.message || `HTTP ${r.status}`;
        // `champ` est un chemin : taux[0].taux, lpp.part_employeur, jours_ouvres.
        // On l'accroche au champ, pas seulement en tête d'écran.
        if (r.status === 400) { setChampErreur({ champ: j.champ || null, message: msg }); return; }
        throw new Error(msg);
      }
      setOrig(b);
      showNotification?.(j.message || 'Barème enregistré — il s\'applique aux prochains calculs de coût.', 'success');
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  // Estimation locale, recalculée à chaque frappe.
  const local = useMemo(
    () => (b ? estimerCout(b, { salaire: essai.salaire, age: num(essai.age), joursOuvres: essai.jours || b.jours_ouvres }) : null),
    [b, essai],
  );
  // Simulation serveur : demandée à la demande, jamais en boucle sur la frappe.
  const simuler = async () => {
    try {
      const r = await fetch(`${BASE}/config/couts/simulation`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ salaire_annuel: num(essai.salaire), age: num(essai.age), jours_ouvres: num(essai.jours || b.jours_ouvres) }),
      });
      if ([404, 405, 501].includes(r.status)) { showNotification?.('La simulation serveur arrive — l\'estimation affichée reste locale.', 'info'); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      const d = j.data ?? j;
      setServeur(d);
      // Un coût qui paraît complet alors qu'il manque des paramètres est pire
      // qu'un coût absent : on reprend ce que le serveur dit qu'il n'a pas.
      if (d.incomplet) {
        const manque = Array.isArray(d.manque) ? d.manque : [];
        showNotification?.(manque.length
          ? `Simulation incomplète — il manque : ${manque.map(m => (typeof m === 'string' ? m : m?.libelle || m?.champ)).filter(Boolean).join(', ')}.`
          : 'Simulation incomplète — des paramètres du barème manquent.', 'error');
      } else {
        showNotification?.('Simulation calculée par le serveur.', 'success');
      }
    } catch (e) { showNotification?.(e.message, 'error'); }
  };
  useEffect(() => { setServeur(null); }, [b, essai]);

  // `champ` du 400 est un chemin (taux[0].taux, lpp.part_employeur, …) : le
  // message se pose SOUS l'entrée fautive, pas seulement en haut de l'écran.
  const champ = (valeur, onChange, props = {}) => {
    const faux = props.chemin && champErreur?.champ === props.chemin;
    return (
      <span style={{ display: 'block', minWidth: 0 }}>
        <input className={`erp-field${props.mono === false ? '' : ' erp-mono'}`} inputMode="decimal"
          value={valeur ?? ''} aria-invalid={faux || undefined}
          onChange={e => { onChange(e.target.value); if (faux) setChampErreur(null); }}
          placeholder={props.placeholder || ''}
          style={{ width: '100%', textAlign: 'right', ...(faux ? { borderColor: 'var(--danger-bd)' } : null), ...(props.style || {}) }}
          aria-label={props.label} />
        {faux && <span role="alert" style={{ display: 'block', marginTop: '4px', fontSize: '10.5px', lineHeight: 1.4, color: 'var(--danger-fg)' }}>{champErreur.message}</span>}
      </span>
    );
  };

  if (b === null) return <div style={{ padding: '20px', color: 'var(--erp-text-tertiary)', fontSize: '13px' }}><BI icon="arrow-repeat" /> Chargement du barème…</div>;

  const affiche = serveur ? {
    cout_annuel: serveur.cout_annuel ?? serveur.coutAnnuel, cjm: serveur.cjm,
    detail: Array.isArray(serveur.detail) ? serveur.detail : local.detail,
    brut: local.brut, jours: serveur.jours_ouvres ?? local.jours,
    coefficient: serveur.coefficient ?? (local.brut ? (serveur.cout_annuel ?? 0) / local.brut : 0),
  } : local;

  return (
    <div className="couts-grille">
      <div>
        <p style={{ margin: '0 0 14px', fontSize: '13px', lineHeight: 1.6, color: 'var(--erp-text-secondary)', maxWidth: '70ch' }}>
          Ce barème sert à tout ce qui parle d'argent : le coût journalier d'un contrat, la marge d'une mission,
          et demain la comptabilité. Il se règle ici une fois. <strong style={{ color: 'var(--erp-ink)' }}>Les taux changent
          chaque année et selon la caisse et le canton</strong> — saisissez ceux de vos décomptes, rien n'est prérempli.
        </p>

        {!absent && !configure && (
          <div style={{ marginBottom: '14px', padding: '11px 13px', borderRadius: 'var(--erp-radius-sm)', border: '1px solid color-mix(in srgb,var(--warn) 45%,transparent)', background: 'color-mix(in srgb,var(--warn) 7%,transparent)', color: 'var(--warn)', fontSize: '12.5px', fontWeight: 600 }}>
            <BI icon="exclamation-triangle-fill" style={{ marginRight: '6px' }} />
            Barème pas encore renseigné — tant qu'il l'est pas, tout coût et toute marge affichés ailleurs sont incomplets.
          </div>
        )}
        {absent && (
          <div style={{ marginBottom: '14px', padding: '11px 13px', border: '1px solid var(--erp-line)', borderRadius: 'var(--erp-radius-sm)', background: 'var(--erp-surface-muted)', fontSize: '12.5px', color: 'var(--erp-text-secondary)' }}>
            Le barème arrive côté serveur. L'écran est prêt : vos saisies aboutiront dès la mise en ligne, rien n'est enregistré d'ici là.
          </div>
        )}
        {champErreur && !champErreur.champ && (
          <div role="alert" style={{ marginBottom: '14px', padding: '11px 13px', border: '1px solid var(--danger-bd)', borderRadius: 'var(--erp-radius-sm)', background: 'var(--danger-bg)', color: 'var(--danger-fg)', fontSize: '12.5px' }}>{champErreur.message}</div>
        )}

        {/* 1 — les charges proportionnelles au brut */}
        <section className="couts-bloc">
          <div className="couts-bloc-tete"><span style={LBL}>Charges sociales — part employeur</span><span className="couts-bloc-note">en % du salaire brut annuel</span></div>
          <div className="couts-lignes">
            <div className="couts-ligne couts-ligne--tete"><span>Charge</span><span>Taux %</span><span>Plafond annuel</span><span>Sur l'essai</span></div>
            {b.taux.map((l, i) => {
              const std = LIGNES_STD.find(s => s.cle === l.cle);
              const ligne = (local.detail || []).find(d => d.libelle === l.libelle);
              return (
                <div className="couts-ligne" key={l.cle}>
                  <span className="couts-ligne-nom">{l.libelle}</span>
                  {champ(l.taux, v => majTaux(i, { taux: v }), { placeholder: std?.indice || '0.0', label: `Taux ${l.libelle}`, chemin: `taux[${i}].taux` })}
                  {champ(l.plafond, v => majTaux(i, { plafond: v }), { placeholder: std?.plafondIndice || 'aucun', label: `Plafond ${l.libelle}`, chemin: `taux[${i}].plafond` })}
                  <span className="couts-ligne-val erp-mono">{ligne ? chf(ligne.montant) : '—'}</span>
                </div>
              );
            })}
          </div>
        </section>

        {/* 2 — la LPP, seule charge qui dépend de l'âge */}
        <section className="couts-bloc couts-bloc--lpp">
          <div className="couts-bloc-tete"><span style={LBL}>LPP — prévoyance professionnelle</span><span className="couts-bloc-note">la seule charge qui dépend de l'âge</span></div>
          <div className="couts-lpp-params">
            <label><span style={LBL}>Seuil d'accès</span>{champ(b.lpp.seuil_acces, v => majLpp({ seuil_acces: v }), { placeholder: '22 680', chemin: 'lpp.seuil_acces' })}</label>
            <label><span style={LBL}>Déduction de coordination</span>{champ(b.lpp.deduction_coordination, v => majLpp({ deduction_coordination: v }), { placeholder: '26 460', chemin: 'lpp.deduction_coordination' })}</label>
            <label><span style={LBL}>Salaire assuré max</span>{champ(b.lpp.salaire_max, v => majLpp({ salaire_max: v }), { placeholder: '90 720', chemin: 'lpp.salaire_max' })}</label>
            <label><span style={LBL}>Coordonné minimum</span>{champ(b.lpp.coordonne_min, v => majLpp({ coordonne_min: v }), { placeholder: '3 780', chemin: 'lpp.coordonne_min' })}</label>
            <label><span style={LBL}>Part employeur %</span>{champ(b.lpp.part_employeur, v => majLpp({ part_employeur: v }), { placeholder: '50', chemin: 'lpp.part_employeur' })}</label>
          </div>
          <div className="couts-tranches">
            {b.lpp.tranches.map((t, i) => {
              const actif = num(essai.age) >= num(t.age_min) && num(essai.age) <= num(t.age_max);
              return (
                <div className={`couts-tranche${actif ? ' is-active' : ''}`} key={`${t.age_min}-${t.age_max}`}>
                  <span className="couts-tranche-age erp-mono">{t.age_min}–{t.age_max} ans</span>
                  {champ(t.taux, v => majTranche(i, { taux: v }), { placeholder: INDICE_TRANCHE[t.age_min] || '0', label: `Taux LPP ${t.age_min} à ${t.age_max} ans`, chemin: `lpp.tranches[${i}].taux` })}
                  <span className="couts-tranche-unite">%</span>
                </div>
              );
            })}
          </div>
          <p className="couts-aide">Salaire coordonné sur l'essai : <b className="erp-mono">{chf(local.coordonne)}</b>{local.tranche ? ` · tranche ${local.tranche.age_min}–${local.tranche.age_max} ans` : ' · aucune tranche pour cet âge'}</p>
        </section>

        {/* 3 — ce qui ne dépend pas du salaire */}
        <section className="couts-bloc">
          <div className="couts-bloc-tete"><span style={LBL}>Frais entreprise</span><span className="couts-bloc-note">tout ce qui coûte sans passer par le salaire</span></div>
          <div className="couts-lpp-params">
            <label><span style={LBL}>Par an</span>{champ(b.frais.annuel, v => maj({ frais: { ...b.frais, annuel: v } }), { placeholder: '0', chemin: 'frais.annuel' })}</label>
            <label><span style={LBL}>Par mois</span>{champ(b.frais.mensuel, v => maj({ frais: { ...b.frais, mensuel: v } }), { placeholder: '0', chemin: 'frais.mensuel' })}</label>
            <label><span style={LBL}>Par jour travaillé</span>{champ(b.frais.journalier, v => maj({ frais: { ...b.frais, journalier: v } }), { placeholder: '0', chemin: 'frais.journalier' })}</label>
            <label><span style={LBL}>Jours ouvrés / an</span>{champ(b.jours_ouvres, v => maj({ jours_ouvres: v }), { placeholder: '225', chemin: 'jours_ouvres' })}</label>
          </div>
        </section>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
          <span style={{ flex: 1, fontSize: '11.5px', color: 'var(--erp-text-tertiary)' }}>
            {dirty ? 'Modifications non enregistrées.' : 'Barème à jour.'}
          </span>
          <button type="button" className="erp-btn erp-btn--primary" onClick={enregistrer} disabled={busy || !dirty}
            style={(busy || !dirty) ? { opacity: 0.5 } : undefined}>
            {busy ? 'Enregistrement…' : 'Enregistrer le barème'}
          </button>
        </div>
      </div>

      {/* Le reçu : ce que le barème fait à un prix de journée, en direct. */}
      <aside className="couts-recu">
        <div className="couts-recu-tete"><span style={LBL}>Cas d'essai</span></div>
        <div className="couts-essai">
          <label><span style={LBL}>Brut annuel</span>{champ(essai.salaire, v => setEssai(e => ({ ...e, salaire: v })), { placeholder: '130 000' })}</label>
          <label><span style={LBL}>Âge</span>{champ(essai.age, v => setEssai(e => ({ ...e, age: v })), { placeholder: '38' })}</label>
          <label><span style={LBL}>Jours ouvrés</span>{champ(essai.jours, v => setEssai(e => ({ ...e, jours: v })), { placeholder: String(b.jours_ouvres || '225') })}</label>
        </div>
        <div className="couts-recu-lignes">
          <div className="couts-recu-ligne"><span>Salaire brut</span><b className="erp-mono">{chf(affiche.brut)}</b></div>
          {(affiche.detail || []).map((d, i) => (
            <div className="couts-recu-ligne" key={`${d.libelle}-${i}`}>
              <span>{d.libelle}{d.note ? <em>{d.note}</em> : null}</span>
              <b className="erp-mono">{chf(d.montant)}</b>
            </div>
          ))}
          <div className="couts-recu-ligne couts-recu-ligne--total"><span>Coût employeur annuel</span><b className="erp-mono">{chf(affiche.cout_annuel)}</b></div>
        </div>
        <div className="couts-cjm">
          <span style={LBL}>Coût journalier moyen</span>
          <strong>{chf2(affiche.cjm)}</strong>
          <span className="couts-cjm-note erp-mono">
            {chf(affiche.cout_annuel)} ÷ {num(affiche.jours)} jours
            {affiche.coefficient ? ` · coefficient ${affiche.coefficient.toFixed(2)}` : ''}
          </span>
        </div>
        {serveur?.incomplet && (
          <div className="couts-recu-manque" role="alert">
            <BI icon="exclamation-triangle-fill" />
            <span>
              Coût incomplet — le serveur signale
              {Array.isArray(serveur.manque) && serveur.manque.length
                ? ` : ${serveur.manque.map(m => (typeof m === 'string' ? m : m?.libelle || m?.champ)).filter(Boolean).join(', ')}.`
                : ' des paramètres manquants.'}
            </span>
          </div>
        )}
        <div className="couts-recu-pied">
          <span>{serveur ? (serveur.incomplet ? 'Calculé par le serveur, sur un barème incomplet.' : 'Calculé par le serveur.') : 'Estimation locale — le serveur fera foi.'}</span>
          <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={simuler}>Vérifier au serveur</button>
        </div>
      </aside>
    </div>
  );
}
